import { ref, readonly, computed, onMounted, onUnmounted, watch, type Ref } from 'vue'
import { ChannelManager } from '@/utils/channels/ChannelManager'
import type {
  ConnectionStatus,
  WebSocketMessage,
  WebSocketError,
  ChannelOptions
} from '@/utils/channels/types'
import type { IChannelSubscription } from '@/utils/channels/ChannelSubscription'

/**
 * Vue composable for managing channel subscriptions with automatic lifecycle management by default
 *
 * @param channelName - The name of the channel to subscribe to (reactive)
 * @param options - Optional channel configuration
 * @param options.disableAutoLifecycle - If true, disables automatic subscribe/unsubscribe on component mount/unmount (default: false)
 * @returns Object with reactive state and control methods
 *
 * @example
 * ```typescript
 * // Basic usage (automatic lifecycle management - default)
 * const { connectionStatus, lastMessage } = useChannelSubscription('case_123')
 * // Automatically subscribes on mount, unsubscribes on unmount
 *
 * // With reactive channel name
 * const caseId = ref('123')
 * const channelName = computed(() => `case_${caseId.value}`)
 * const { connectionStatus, lastMessage } = useChannelSubscription(channelName)
 *
 * // With options
 * const { connectionStatus, lastMessage } = useChannelSubscription('case_123', {
 *   autoReconnect: true,
 *   maxReconnectAttempts: 3
 * })
 *
 * // Manual lifecycle management (for stores, etc.)
 * const { subscribe, unsubscribe } = useChannelSubscription('case_123', {
 *   disableAutoLifecycle: true
 * })
 * // Must manually call subscribe() and unsubscribe()
 * ```
 */
export function useChannelSubscription(
  channelName: string | Ref<string>,
  options?: ChannelOptions & { disableAutoLifecycle?: boolean }
) {
  // Reactive state
  const connectionStatus = ref<ConnectionStatus>('disconnected')
  const lastMessage = ref<WebSocketMessage | null>(null)
  const lastError = ref<WebSocketError | null>(null)
  const reconnectAttempts = ref<number>(0)
  const isSubscribed = ref<boolean>(false)

  // Internal state
  let currentSubscription: IChannelSubscription | null = null
  let subscribedChannelName = ''
  let messageUnsubscriber: (() => void) | null = null
  let statusUnsubscriber: (() => void) | null = null
  let errorUnsubscriber: (() => void) | null = null

  /**
   * Get the current channel name (handle both string and ref)
   */
  const getCurrentChannelName = (): string => {
    return typeof channelName === 'string' ? channelName : channelName.value
  }

  /**
   * Subscribe to the channel
   */
  const subscribe = async (): Promise<void> => {
    const currentChannelName = getCurrentChannelName()

    if (!currentChannelName) {
      console.warn('Cannot subscribe: channel name is empty')
      return
    }

    // already subscribed to this exact channel — skip redundant re-subscribe
    // (prevents the reactive watcher from tearing down a subscription
    //  that was just created by an explicit subscribe() call)
    if (currentSubscription && isSubscribed.value && subscribedChannelName === currentChannelName) {
      return
    }

    // Unsubscribe from previous subscription if exists
    if (currentSubscription) {
      unsubscribe()
    }

    try {
      // Create new subscription
      currentSubscription = ChannelManager.subscribe(currentChannelName, options)

      // Set up message handler
      messageUnsubscriber = currentSubscription.onMessage((message: WebSocketMessage) => {
        lastMessage.value = message
        lastError.value = null // Clear error on successful message
      })

      // Set up status change handler
      statusUnsubscriber = currentSubscription.onStatusChange((status: ConnectionStatus) => {
        connectionStatus.value = status

        // Update reconnect attempts from connection
        const connection = currentSubscription?.getConnection()
        if (connection) {
          reconnectAttempts.value = connection.getReconnectAttempts()
        }
      })

      // Set up error handler
      errorUnsubscriber = currentSubscription.onError((error: WebSocketError) => {
        lastError.value = error

        // Update reconnect attempts on error
        const connection = currentSubscription?.getConnection()
        if (connection) {
          reconnectAttempts.value = connection.getReconnectAttempts()
        }
      })

      // Perform the actual subscription
      await currentSubscription.subscribe()
      subscribedChannelName = currentChannelName
      isSubscribed.value = true
    } catch (error) {
      console.error('Failed to subscribe to channel:', error)
      lastError.value = {
        type: 'unknown',
        message: error instanceof Error ? error.message : 'Failed to subscribe'
      }
      connectionStatus.value = 'error'
      throw error
    }
  }

  /**
   * Unsubscribe from the channel
   */
  const unsubscribe = (): void => {
    if (currentSubscription) {
      currentSubscription.unsubscribe()
      currentSubscription = null
    }

    // Clean up event listeners
    if (messageUnsubscriber) {
      messageUnsubscriber()
      messageUnsubscriber = null
    }

    if (statusUnsubscriber) {
      statusUnsubscriber()
      statusUnsubscriber = null
    }

    if (errorUnsubscriber) {
      errorUnsubscriber()
      errorUnsubscriber = null
    }

    // Reset state
    subscribedChannelName = ''
    isSubscribed.value = false
    connectionStatus.value = 'disconnected'
    reconnectAttempts.value = 0
  }

  /**
   * Manually reconnect to the channel
   */
  const reconnect = async (): Promise<void> => {
    if (currentSubscription) {
      const connection = currentSubscription.getConnection()
      if (connection) {
        connection.disconnect()
        await connection.connect()
      }
    } else {
      await subscribe().catch(() => {
        /* subscribe() sets lastError / connectionStatus */
      })
    }
  }

  /**
   * Send a message through the channel
   */
  const sendMessage = (data: any): void => {
    if (currentSubscription) {
      const connection = currentSubscription.getConnection()
      if (connection && connection.isConnected()) {
        connection.send(data)
      } else {
        console.warn('Cannot send message: not connected to channel')
        lastError.value = {
          type: 'network_error',
          message: 'Cannot send message: not connected to channel'
        }
      }
    }
  }

  /**
   * Clear the last error
   */
  const clearError = (): void => {
    lastError.value = null
  }

  /**
   * Clear the last message
   */
  const clearMessage = (): void => {
    lastMessage.value = null
  }

  // Watch for channel name changes (if channelName is a ref)
  if (typeof channelName !== 'string') {
    watch(channelName, (newChannelName, oldChannelName) => {
      if (newChannelName !== oldChannelName) {
        if (oldChannelName && currentSubscription) {
          unsubscribe()
        }
        if (newChannelName) {
          void subscribe().catch(() => {
            // State is already set in subscribe()
          })
        }
      }
    })
  }

  // Automatic lifecycle management (enabled by default)
  const disableAutoLifecycle = options?.disableAutoLifecycle ?? false

  if (!disableAutoLifecycle) {
    onMounted(() => {
      const currentChannelName = getCurrentChannelName()
      if (currentChannelName) {
        void subscribe().catch(() => {
          // State is already set in subscribe(); avoid unhandled rejection on mount
        })
      }
    })

    onUnmounted(() => {
      unsubscribe()
    })
  }

  // Computed helpers based on connection status
  const isConnected = computed(() => connectionStatus.value === 'connected')
  const isConnecting = computed(() => connectionStatus.value === 'connecting')
  const isReconnecting = computed(() => connectionStatus.value === 'reconnecting')
  const hasError = computed(() => connectionStatus.value === 'error' || lastError.value !== null)

  // Return reactive state and control methods
  return {
    // Reactive state (readonly to prevent external mutation)
    connectionStatus: readonly(connectionStatus),
    lastMessage: readonly(lastMessage),
    lastError: readonly(lastError),
    reconnectAttempts: readonly(reconnectAttempts),
    isSubscribed: readonly(isSubscribed),

    // Control methods
    subscribe,
    unsubscribe,
    reconnect,
    sendMessage,
    clearError,
    clearMessage,

    // Computed helpers
    isConnected: readonly(isConnected),
    isConnecting: readonly(isConnecting),
    isReconnecting: readonly(isReconnecting),
    hasError: readonly(hasError)
  }
}

/**
 * Specialized composable for case channel subscriptions
 *
 * @param caseUuid - The UUID of the case to subscribe to (reactive)
 * @param options - Optional channel configuration
 * @returns Same as useChannelSubscription but with case-specific channel naming
 *
 * @example
 * ```typescript
 * const caseId = ref('550e8400-e29b-41d4-a716-446655440000')
 * const { connectionStatus, lastMessage } = useCaseChannelSubscription(caseId)
 * ```
 */
export function useCaseChannelSubscription(
  caseUuid: string | Ref<string>,
  options?: ChannelOptions & { disableAutoLifecycle?: boolean }
) {
  /**
   * Validates if a UUID is valid (not empty, null, undefined, or just whitespace)
   */
  const isValidUuid = (uuid: string): boolean => {
    return Boolean(uuid && typeof uuid === 'string' && uuid.trim().length > 0)
  }

  // Convert case UUID to channel name, but only if UUID is valid
  const channelName = computed(() => {
    const currentUuid = typeof caseUuid === 'string' ? caseUuid : caseUuid.value
    return isValidUuid(currentUuid) ? `case_${currentUuid}` : ''
  })

  return useChannelSubscription(channelName, options)
}
